import { coinflipStore } from '@/stores/coinflip.store';
import { useSyncedCountdown } from '@/utils/hooks/use-synced-countdown';

interface Props {
    tradeId: number;
    coinflipGameId?: number;
}

const SteamTrade = ({ tradeId, coinflipGameId }: Props) => {
    const game = coinflipStore((state) =>
        coinflipGameId == null ? undefined : state.games.get(coinflipGameId)
    );
    const seconds = useSyncedCountdown(game?.pendingJoin?.expiresAt ?? null, 120, 'floor');
    function steam() {
        window.open('steam://url/ShowTradeOffer/' + tradeId, '_blank')?.focus();
    }

    function browser() {
        window.open('https://steamcommunity.com/tradeoffer/' + tradeId, '_blank')?.focus();
    }

    return (
        <div className="flex items-center flex-col">
            {/* Neon shield — matches the brand line-art look */}
            <div className="relative grid place-items-center w-[92px] h-[92px] mb-1">
                <span
                    aria-hidden
                    className="absolute inset-0 rounded-[34%] blur-[16px] opacity-60 -z-[1]"
                    style={{
                        background:
                            'radial-gradient(circle, rgba(139,109,255,0.75), transparent 70%)',
                    }}
                />
                <svg
                    viewBox="0 0 24 24"
                    className="relative w-[70px] h-[70px]"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        <linearGradient id="stShield" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0" stopColor="#8b6dff" />
                            <stop offset="0.55" stopColor="#b78dff" />
                            <stop offset="1" stopColor="#3ad6ff" />
                        </linearGradient>
                        <filter id="stShieldGlow" x="-60%" y="-60%" width="220%" height="220%">
                            <feGaussianBlur stdDeviation="0.6" result="b" />
                            <feMerge>
                                <feMergeNode in="b" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    <g
                        fill="none"
                        stroke="url(#stShield)"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        filter="url(#stShieldGlow)"
                    >
                        <path d="M12 2 L20 5 V11 C20 16 16.5 19.5 12 22 C7.5 19.5 4 16 4 11 V5 Z" />
                        <path d="M8.6 12 l2.4 2.4 l4.4 -5" />
                    </g>
                </svg>
            </div>

            <p className="font-chat-nick font-semibold mt-3 mb-7 text-text_2 text-[15px] tracking-[0.02em]">
                {game?.pendingJoin ? `Confirm the trade within ${seconds}s` : 'Confirm this trade'}
            </p>

            <div className="flex gap-3 px-8 justify-center mb-2 max-md:px-0 max-md:w-full max-md:flex-col">
                {/* OPEN IN BROWSER — secondary (neutral) */}
                <button
                    onClick={browser}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-fl text-[13px] font-bold tracking-[0.06em] border border-stroke bg-layer_2 text-text_2 hover:bg-layer_3 hover:text-text_main hover:border-stroke_strong hover:-translate-y-0.5 transition-all"
                >
                    <svg
                        viewBox="0 0 24 24"
                        className="w-4 h-4 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
                    >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M3 12h18" />
                        <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
                    </svg>
                    OPEN IN BROWSER
                </button>

                {/* OPEN IN STEAM — primary green CTA */}
                <button
                    onClick={steam}
                    className="group relative overflow-hidden inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-[13px] font-extrabold tracking-[0.08em] text-[#04150c] hover:-translate-y-0.5 transition-transform"
                    style={{
                        background:
                            'linear-gradient(135deg, #7df3b4 0%, #46e08a 45%, #1f9f7a 100%)',
                        boxShadow:
                            'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 0 0 1px rgba(255,255,255,0.16), 0 0 26px -4px rgba(70,224,138,0.8), 0 8px 22px -6px rgba(31,159,122,0.6)',
                    }}
                >
                    {/* glossy shine sweep on hover */}
                    <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
                        style={{
                            background:
                                'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.6), transparent 70%)',
                        }}
                    />
                    <svg
                        viewBox="0 0 24 24"
                        className="relative w-[18px] h-[18px] fill-current"
                        aria-hidden
                    >
                        <path d="M11.98 0C5.67 0 .51 4.86.02 11.04l6.43 2.66c.55-.37 1.2-.59 1.91-.59.07 0 .13 0 .19.01l2.86-4.14v-.06c0-2.5 2.03-4.52 4.52-4.52 2.5 0 4.53 2.03 4.53 4.53s-2.03 4.52-4.53 4.52h-.1l-4.08 2.91v.16c0 1.87-1.51 3.4-3.39 3.4-1.63 0-3.02-1.18-3.33-2.73L.44 15.27C1.86 20.31 6.49 24 11.98 24 18.6 24 24 18.63 24 12S18.6 0 11.98 0zM7.54 18.21l-1.47-.61c.26.54.71 1 1.31 1.25 1.3.54 2.8-.08 3.34-1.37.26-.63.26-1.32 0-1.95s-.75-1.12-1.38-1.38c-.62-.26-1.29-.25-1.88-.03l1.52.63c.96.4 1.41 1.5 1.01 2.46-.4.95-1.5 1.4-2.46 1.01zm11.42-9.3c0-1.66-1.36-3.02-3.02-3.02-1.66 0-3.01 1.36-3.01 3.02 0 1.67 1.35 3.02 3.01 3.02 1.67 0 3.02-1.35 3.02-3.02zm-5.27 0c0-1.25 1.01-2.27 2.26-2.27 1.25 0 2.27 1.02 2.27 2.27 0 1.25-1.02 2.26-2.27 2.26-1.25 0-2.26-1.01-2.26-2.26z" />
                    </svg>
                    OPEN IN STEAM
                </button>
            </div>
        </div>
    );
};

export default SteamTrade;
